import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Operation } from '../_models/operation';
import { OperationProof, OperationProofWithFile } from '../_models/operationProof';
import { OperationType } from '../_models/operationType';
import { Product } from '../_models/product';
import { ProductOperation } from '../_models/productOperation';
import { Vehicle } from '../_models/vehicle';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class OperationService {
  private apiUrl = `${environment.apiUrl}/Operations`;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getById(operationId: string): Observable<Operation> {
    return this.http.get<Operation>(`${this.apiUrl}/${operationId}`, { headers: this.authService.headers });
  }

  add(solutionId: string, operation: Operation) {
    return this.http.post<Operation>(`${environment.apiUrl}/Operations/${solutionId}`, operation, { headers: this.authService.headers });
  }

  delete(operationId: string) {
    return this.http.delete(`${environment.apiUrl}/Operations/${operationId}`, { headers: this.authService.headers });
  }

  update(operationId: string, operation: Operation): Observable<Operation> {
    return this.http.put<Operation>(`${this.apiUrl}/${operationId}`, operation, { headers: this.authService.headers });
  }

  getOperationTypes() {
    return this.http.get<OperationType[]>(`${environment.apiUrl}/operations/OperationType`, { headers: this.authService.headers });
  }

  getProduitsSortants(OperationType?: string) {
    return this.http.get<Product[]>(`${environment.apiUrl}/operations/ProduitsSortants?OperationType=${OperationType}`, { headers: this.authService.headers });
  }

  getProduitsEntrants(OperationType?: string, versionId?: string) {
    const params = new URLSearchParams();
    if (OperationType) params.append('OperationType', OperationType);
    if (versionId) params.append('VersionId', versionId);
    return this.http.get<Product[]>(`${environment.apiUrl}/operations/ProduitsEntrants?${params.toString()}`, { headers: this.authService.headers });
  }

  duplicate(resourceId: string) {
    return this.http.post<Operation>(`${environment.apiUrl}/Operations/duplicate?id=${resourceId}`, null, { headers: this.authService.headers });
  }

  editRank(operations?: Operation[]) {
    return this.http.put(`${environment.apiUrl}/operations/editRankOperation`, operations, { headers: this.authService.headers }).toPromise();
  }

  copyOperation(operation: Operation, solutionId: string) {
    return this.http.post<Operation>(`${environment.apiUrl}/Operations/copy/${solutionId}`, operation, { headers: this.authService.headers });
  }

  //Inbound products management
  addInboundProduct(operationId: string, product: ProductOperation): Observable<ProductOperation> {
    return this.http.post<ProductOperation>(`${this.apiUrl}/${operationId}/inboundProduct`, product, { headers: this.authService.headers });
  }

  updateInboundProduct(operationId: string, productId: string, product: ProductOperation): Observable<ProductOperation> {
    return this.http.put<ProductOperation>(`${this.apiUrl}/${operationId}/inboundProduct/${productId}`, product, { headers: this.authService.headers });
  }

  deleteInboundProduct(operationId: string, productId: string): Observable<Operation> {
    return this.http.delete<Operation>(`${this.apiUrl}/${operationId}/inboundProduct/${productId}`, { headers: this.authService.headers });
  }

  //Outbound products management
  addOutboundProduct(operationId: string, product: ProductOperation): Observable<ProductOperation> {
    return this.http.post<ProductOperation>(`${this.apiUrl}/${operationId}/outboundProduct`, product, { headers: this.authService.headers });
  }

  updateOutboundProduct(operationId: string, productId: string, product: ProductOperation): Observable<ProductOperation> {
    return this.http.put<ProductOperation>(`${this.apiUrl}/${operationId}/outputProduct/${productId}`, product, { headers: this.authService.headers });
  }

  deleteOutboundProduct(operationId: string, productId: string): Observable<Operation> {
    return this.http.delete<Operation>(`${this.apiUrl}/${operationId}/outputProduct/${productId}`, { headers: this.authService.headers });
  }

  //Vehicle management
  addVehicle(operationId: string, vehicle: Vehicle): Observable<Vehicle> {
    return this.http.post<Vehicle>(`${this.apiUrl}/${operationId}/vehicle`, vehicle, { headers: this.authService.headers });
  }

  updateVehicle(operationId: string, vehicleId: string, vehicle: Vehicle): Observable<Vehicle> {
    return this.http.put<Vehicle>(`${this.apiUrl}/${operationId}/vehicle/${vehicleId}`, vehicle, { headers: this.authService.headers });
  }

  deleteVehicle(operationId: string, vehicleId: string): Observable<Operation> {
    return this.http.delete<Operation>(`${this.apiUrl}/${operationId}/vehicle/${vehicleId}`, { headers: this.authService.headers });
  }

  //Proofs management
  getCompleteProof(operationId: string, proofId: string): Observable<OperationProofWithFile> {
    return this.http.get<OperationProofWithFile>(`${this.apiUrl}/${operationId}/proof/${proofId}`, { headers: this.authService.headers });
  }

  addProof(operationId: string, proof: OperationProofWithFile): Observable<Operation> {
    return this.http.post<Operation>(`${this.apiUrl}/${operationId}/proof`, proof, { headers: this.authService.headers });
  }

  addBatchProofs(operationId: string, proofs: OperationProofWithFile[]): Observable<Operation[]> {
    return this.http.post<Operation[]>(`${this.apiUrl}/${operationId}/proofBatch`, proofs, { headers: this.authService.headers });
  }

  updateProof(operationId: string, proofId: string, proof: OperationProof): Observable<Operation> {
    return this.http.put<Operation>(`${this.apiUrl}/${operationId}/proof/${proofId}`, proof, { headers: this.authService.headers });
  }

  deleteProof(operationId: string, proofId: string): Observable<Operation> {
    return this.http.delete<Operation>(`${this.apiUrl}/${operationId}/proof/${proofId}`, { headers: this.authService.headers });
  }
}
